import React from 'react'
import { Component, LocaleCode } from '../types/component'
import { ComponentRenderer } from './ComponentRenderer'

interface SectionProps {
  component: Component
  style: React.CSSProperties
  locale?: LocaleCode
  storeId: string
  apiUrl: string
  cartId?: string | null
  wishlistId?: string | null
  onCartUpdate?: (cartId: string) => void
  onWishlistUpdate?: (wishlistId: string) => void
}

/**
 * Flexible Section component that accepts any CSS properties
 * This allows AI and users to create complex, custom layouts
 */
export function Section({
  component,
  style,
  locale = 'en',
  storeId,
  apiUrl,
  cartId,
  wishlistId,
  onCartUpdate,
  onWishlistUpdate
}: SectionProps) {
  const config = (component.config as Record<string, unknown>) || {}
  const tag = (config.tag as string) || 'section'

  // Render children if they exist
  const children = component.children?.map((child) => (
    <ComponentRenderer
      key={child.id}
      component={child}
      locale={locale}
      storeId={storeId}
      apiUrl={apiUrl}
      cartId={cartId}
      wishlistId={wishlistId}
      onCartUpdate={onCartUpdate}
      onWishlistUpdate={onWishlistUpdate}
    />
  ))

  // Support custom HTML attributes
  const htmlAttributes: Record<string, any> = {}
  if (config.id) htmlAttributes.id = config.id
  if (config.ariaLabel) htmlAttributes['aria-label'] = config.ariaLabel
  if (config.role) htmlAttributes.role = config.role

  // Create element with custom tag
  const Element = tag as keyof JSX.IntrinsicElements

  return (
    <Element
      style={style}
      {...htmlAttributes}
    >
      {children}
    </Element>
  )
}
